import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router";
import { useStore } from "@/app/store";
import { useAppToast } from "@/components/AppToast";
import useCalculationDetail from "../hooks/useCalculationDetail";
import useSaveInputsAndExecute from "../hooks/useSaveInputsAndExecute";
import useEditCalculationDetails from "../hooks/useEditCalculationDetails";
import useCalculationLifecycle from "../hooks/useCalculationLifecycle";
import useResultRows from "../hooks/useResultRows";
import { routePaths } from "@/constants/routePaths";
import { getReturnPath } from "@/utils/getReturnPath";
import CalculationInfoBar from "./CalculationInfoBar/CalculationInfoBar.component";
import CalculationCategoryTabs from "./CalculationCategoryTabs/CalculationCategoryTabs.component";
import CalculationResultsTable from "./CalculationResultsTable/CalculationResultsTable.component";
import CostPanelContainer, {
  type CostPanelHandle,
} from "./CostPanel/CostPanelContainer.component";
import { saveFailedMessage } from "./CostPanel/CostPanel.constants";
import StaleVersionBanner from "./StaleVersionBanner/StaleVersionBanner.component";
import CalculationNotFound from "./CalculationNotFound/CalculationNotFound.component";
import DelayedSpinner from "@/components/DelayedSpinner";

interface CalculationDetailContainerProps {
  calculationId: string;
}

const CalculationDetailContainer: React.FC<CalculationDetailContainerProps> = ({
  calculationId,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const returnPath = getReturnPath(location.state, routePaths.calculations);

  const calculation = useStore((s) =>
    s.calculation?.id === calculationId ? s.calculation : null,
  );
  const clearCalculation = useStore((s) => s.clearCalculation);

  const {
    categoryGroups,
    activeCategoryId,
    activeSubcategoryId,
    loading,
    error,
    refetchCalculation,
    onCategoryChange,
    onSubcategoryChange,
  } = useCalculationDetail(calculationId);

  const [conflictBanner, setConflictBanner] = useState(false);
  const handleConflict = useCallback(() => setConflictBanner(true), []);
  const handleReload = useCallback(async () => {
    await refetchCalculation();
    setConflictBanner(false);
  }, [refetchCalculation]);
  const handleDismissBanner = useCallback(async () => {
    await refetchCalculation();
    setConflictBanner(false);
  }, [refetchCalculation]);

  const { addToast } = useAppToast();
  const notifyInputsSaved = useCallback(
    () =>
      addToast({
        appearance: "success",
        message: "Input values have been updated.",
      }),
    [addToast],
  );
  const notifySaveFailed = useCallback(
    () => addToast({ appearance: "danger", message: saveFailedMessage }),
    [addToast],
  );

  const { saveInputsAndExecute, loading: isSaving } = useSaveInputsAndExecute(
    handleConflict,
    { onSuccess: notifyInputsSaved, onError: notifySaveFailed },
  );

  const { editDetails, isSaving: isDetailsSaving } =
    useEditCalculationDetails(handleConflict);

  const {
    markComplete,
    recalculate,
    reopen,
    inFlight,
    completeError,
    recalculateError,
  } = useCalculationLifecycle(handleConflict);

  const { monthColumns, resultRows } = useResultRows(
    calculationId,
    activeSubcategoryId,
  );

  const addCostPanelRef = useRef<CostPanelHandle>(null);

  const activeSubcategoryHasCosts = useMemo(() => {
    const category = calculation?.categories.find(
      (cat) => cat.subCategoryDefinitionId === activeSubcategoryId,
    );
    return (
      category?.variableInputs.some((input) =>
        input.valuesByDimension.some(
          (entry) =>
            entry.singleValue != null ||
            (entry.monthValues?.some((month) => month.value != null) ?? false),
        ),
      ) ?? false
    );
  }, [calculation, activeSubcategoryId]);

  useEffect(() => () => clearCalculation(), [clearCalculation]);

  if (loading && !calculation) return <DelayedSpinner />;
  if (error || !calculation) return <CalculationNotFound />;

  const incompleteReason = calculation.incompleteReason;
  const hasIncompleteResults = incompleteReason !== "NONE";

  return (
    <>
      {conflictBanner && (
        <StaleVersionBanner
          onReload={handleReload}
          onDismiss={handleDismissBanner}
        />
      )}
      <CalculationInfoBar
        key={calculation.version}
        name={calculation.name}
        kamName={calculation.kamName}
        line={calculation.line}
        startDate={calculation.startDate}
        endDate={calculation.endDate}
        updatedAt={calculation.updatedAt}
        status={calculation.status}
        hasIncompleteResults={hasIncompleteResults}
        incompleteReason={incompleteReason}
        activeSubcategoryHasCosts={activeSubcategoryHasCosts}
        inFlight={inFlight}
        completeError={completeError}
        recalculateError={recalculateError}
        isSaving={isDetailsSaving}
        onBack={() => navigate(returnPath)}
        onAddCost={() => addCostPanelRef.current?.open()}
        onMarkComplete={markComplete}
        onRecalculate={recalculate}
        onReopen={reopen}
        onSaveDetails={editDetails}
      />
      <CalculationCategoryTabs
        categoryGroups={categoryGroups}
        activeCategoryId={activeCategoryId}
        activeSubcategoryId={activeSubcategoryId}
        onCategoryChange={onCategoryChange}
        onSubcategoryChange={onSubcategoryChange}
      />
      <CalculationResultsTable monthColumns={monthColumns} rows={resultRows} />
      <CostPanelContainer
        ref={addCostPanelRef}
        categoryGroups={categoryGroups}
        pageActiveCategoryId={activeCategoryId}
        pageActiveSubcategoryId={activeSubcategoryId}
        onConflict={handleConflict}
        onSave={saveInputsAndExecute}
        isSaving={isSaving}
      />
    </>
  );
};

export default CalculationDetailContainer;
